/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 */

// fortee-bg-touchup.js - quick 260819-bb8. The mask-editing engine behind the
// customer-facing "Touch up" surface: connected-component labelling, the Magic
// piece grabber, the soft brush, the ghost compositor and the undo diff.
//
// WHY THIS EXISTS. assets/fortee-bg-retention.js made a mangled cutout
// impossible to SHIP; it cannot make a nearly-right cutout right. When the guard
// rejects, the customer keeps their original background and a human is told - a
// safe outcome, not a good one. This is the tool that lets the customer finish
// the job themselves in about four taps.
//
// PURE: no DOM, no Alpine, no canvas. Every function takes explicit
// (width, height, typed array) exactly like fortee-bg-retention.js, and for the
// same reason: the properties that matter here are pixel-count properties, and
// they can only be pinned in CI (scripts/test-bg-touchup.cjs) if the same code
// runs under plain `node`. The DOM half lives in assets/fortee-touchup-ui.js.
//
// THE ONE INVARIANT THAT MUST SURVIVE EVERY EDIT TO THIS FILE:
//
//   Magic can never restore background.
//
// grabComponent only ever raises a pixel's alpha TO contentSoft[i], and only for
// pixels the labeller already decided are artwork. So for every pixel
//     result[i] <= max(baseAlpha[i], contentSoft[i])
// with overshoot measured at exactly 0 px on the Martel fixture. That is what
// makes a one-tap restore safe to hand to a customer on a phone: the worst a
// sloppy swipe can do is restore MORE ARTWORK, never more background. Any
// "improvement" that lets a grab exceed contentSoft breaks the tool's safety
// story, and scripts/test-bg-touchup.cjs asserts it directly.
//
// THE OTHER INVARIANT: the ghost never leaks. `alpha` is the truth and is the
// only thing that is ever saved; `ghost` exists solely to tint the screen so the
// customer can see WHERE to paint. compositeShown() writes into a display buffer
// and never touches `alpha`, so toggling the ghost cannot change the result -
// also asserted in CI.

(function () {
  'use strict';

  // Alpha the ghost paints onto artwork that is currently cut away. 100/255 is
  // ~39%: readable over a checkerboard, and still unmistakably weaker than
  // restored artwork so nobody mistakes a ghost for a fix.
  var GHOST_ALPHA = 100;

  // Magic's DEFAULT search radius, in SOURCE pixels, and the floor the caller
  // should never go below. Deliberately small: a sloppy swipe across a word
  // should grab its letters, not a glow three inches away. It is not a brush
  // size and is not exposed to the customer.
  //
  // Callers pass their own radius because "three inches away" is a measure of
  // the SCREEN, not of the file. A 2400px print file shown 350px wide on a phone
  // makes 9 source pixels about one visible pixel, so a fixed source radius would
  // demand pixel-perfect aim from a gloved thumb - see _tuMagicRadius in
  // assets/fortee-touchup-ui.js, which converts a screen distance into this one.
  var MAGIC_RADIUS = 9;

  // Ghosting is only meaningful when "artwork" means something. On a
  // photographic background nearly the whole frame reads as artwork, and on a
  // blank frame nothing does; in both cases ghost everything rather than
  // nothing, so the tool still shows what has been erased.
  var GHOST_DEGENERATE_HI = 0.85;
  var GHOST_DEGENERATE_LO = 0.02;

  // A loaded cutout with less than this share of non-opaque pixels is not a
  // cutout at all - it is the customer's original, because removal failed or was
  // rejected. Same 2% threshold as _canvasHasAlpha in fortee-logo-pipeline.js,
  // deliberately: one definition of "this file already has transparency".
  var CUTOUT_MIN_TRANSPARENT = 0.02;

  var OPAQUE_ALPHA = 128;   // the counting threshold for the "restored" readout

  function retention() {
    if (typeof window !== 'undefined' && window.forteeBgRetention) return window.forteeBgRetention;
    if (typeof global !== 'undefined' && global.window && global.window.forteeBgRetention) {
      return global.window.forteeBgRetention;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // Frame analysis
  // ---------------------------------------------------------------------------

  // Everything the tool needs to know about the customer's ORIGINAL, computed
  // once when the surface opens.
  //
  // contentSoft and the gate both come straight from fortee-bg-retention.js.
  // That is not laziness - it is the whole point. The soft ramp here has to be
  // the SAME ramp the automatic union used, or "Fix artwork" would produce a
  // different cutout by hand than the pipeline produced automatically, and the
  // customer would be able to see the seam.
  //
  // canFix is R.canUnion, the module's one named gate. It is read here to decide
  // whether the Fix button is even offered; the gate itself is still enforced
  // inside unionWithContent, which is the only place allowed to enforce it.
  function analyzeFrame(w, h, rgba) {
    var R = retention();
    var n = Math.max(0, (w | 0) * (h | 0));
    var empty = {
      profile: null, canFix: false, contentShare: 0,
      contentSoft: new Uint8ClampedArray(n), ghost: new Uint8ClampedArray(n)
    };
    if (!R || !rgba || !n) return empty;

    var profile = R.backgroundProfile(w, h, rgba);
    var contentSoft = R.contentAlpha(w, h, rgba, profile);
    var mask = R.contentMask(w, h, rgba, profile);
    var ghost = new Uint8ClampedArray(n);
    var i;
    for (i = 0; i < n; i++) ghost[i] = mask.mask[i] ? GHOST_ALPHA : 0;

    var share = n ? (mask.count / n) : 0;
    if (share > GHOST_DEGENERATE_HI || share < GHOST_DEGENERATE_LO) {
      for (i = 0; i < n; i++) ghost[i] = GHOST_ALPHA;
    }

    return {
      profile: profile,
      canFix: R.canUnion(profile),
      contentShare: share,
      contentSoft: contentSoft,
      ghost: ghost
    };
  }

  // Nearest-neighbour resample of a candidate frame's ALPHA onto the original's
  // grid. Removal engines are under no obligation to return at source
  // resolution, exactly as scoreRetention/unionWithContent already assume.
  function sampleAlphaOnto(dstW, dstH, srcW, srcH, srcRgba) {
    dstW = dstW | 0; dstH = dstH | 0; srcW = srcW | 0; srcH = srcH | 0;
    var out = new Uint8ClampedArray(Math.max(0, dstW * dstH));
    if (!srcRgba || srcW <= 0 || srcH <= 0 || dstW <= 0 || dstH <= 0) return out;
    for (var y = 0; y < dstH; y++) {
      var sy = Math.floor(y * srcH / dstH); if (sy >= srcH) sy = srcH - 1;
      var row = y * dstW;
      var srcRow = sy * srcW;
      for (var x = 0; x < dstW; x++) {
        var sx = Math.floor(x * srcW / dstW); if (sx >= srcW) sx = srcW - 1;
        out[row + x] = srcRgba[(srcRow + sx) * 4 + 3];
      }
    }
    return out;
  }

  // Share of pixels that are not fully opaque. Decides whether the loaded
  // "cutout" is a real cutout or just the customer's original wearing the name.
  function transparentShare(alpha) {
    if (!alpha || !alpha.length) return 0;
    var t = 0;
    for (var i = 0; i < alpha.length; i++) if (alpha[i] < 250) t++;
    return t / alpha.length;
  }

  function isRealCutout(alpha) {
    return transparentShare(alpha) > CUTOUT_MIN_TRANSPARENT;
  }

  function opaqueCount(alpha) {
    var c = 0;
    if (!alpha) return 0;
    for (var i = 0; i < alpha.length; i++) if (alpha[i] > OPAQUE_ALPHA) c++;
    return c;
  }

  // ---------------------------------------------------------------------------
  // Connected components - the Magic tool
  // ---------------------------------------------------------------------------

  // Label every connected piece of artwork once, so Magic restores a whole
  // letter, streak or phone number from a single touch instead of asking a man
  // in work gloves to trace it on a cracked phone.
  //
  // Union-find over horizontal RUNS, 8-connected, one pass. Runs rather than
  // pixels because a logo is mostly long horizontal spans, so this is roughly an
  // order of magnitude fewer union operations than per-pixel labelling and needs
  // no recursion (a flood fill over a 2400px frame overflows the stack).
  //
  // The pixels of each component are then bucketed into one flat Int32Array, so
  // a Magic touch is an array slice rather than a scan of the whole frame.
  function buildComponents(w, h, contentSoft) {
    w = w | 0; h = h | 0;
    var total = Math.max(0, w * h);
    var empty = {
      labels: new Int32Array(0), compStart: new Int32Array(1),
      compPix: new Int32Array(0), compCount: 0
    };
    if (!contentSoft || !total) return empty;

    var parent = new Int32Array(Math.max(16, total >> 2));
    var pcount = 0;

    function grow() {
      if (pcount >= parent.length) {
        var big = new Int32Array(parent.length * 2);
        big.set(parent);
        parent = big;
      }
    }
    function find(x) {
      var r = x;
      while (parent[r] !== r) r = parent[r];
      while (parent[x] !== r) { var nx = parent[x]; parent[x] = r; x = nx; }
      return r;
    }
    function union(a, b) {
      var ra = find(a), rb = find(b);
      if (ra !== rb) parent[rb] = ra;
    }

    var runLabel = new Int32Array(total);
    runLabel.fill(-1);
    var prev = [], cur, y, x, k;

    for (y = 0; y < h; y++) {
      cur = [];
      x = 0;
      var row = y * w;
      while (x < w) {
        while (x < w && contentSoft[row + x] <= 127) x++;
        if (x >= w) break;
        var s = x;
        while (x < w && contentSoft[row + x] > 127) x++;
        grow();
        var lab = pcount++;
        parent[lab] = lab;
        cur.push(s, x, lab);
        for (k = s; k < x; k++) runLabel[row + k] = lab;
      }
      // Merge this row's runs with the previous row's overlapping runs. Both
      // lists are sorted by x, so one interleaved walk is enough - 8-connected
      // because the overlap test uses >= / <= rather than > / <.
      var i = 0, j = 0;
      while (i < prev.length && j < cur.length) {
        var ps = prev[i], pe = prev[i + 1], pl = prev[i + 2];
        var cs = cur[j], ce = cur[j + 1], cl = cur[j + 2];
        if (pe >= cs && ce >= ps) union(pl, cl);
        if (pe < ce) i += 3; else j += 3;
      }
      prev = cur;
    }

    var labels = new Int32Array(total);
    labels.fill(-1);
    var rootOf = new Int32Array(pcount);
    var seen = new Int32Array(pcount);
    seen.fill(-1);
    var nComp = 0, p;
    for (p = 0; p < pcount; p++) {
      var r = find(p);
      if (seen[r] === -1) seen[r] = nComp++;
      rootOf[p] = seen[r];
    }

    var counts = new Int32Array(nComp + 1);
    var idx;
    for (idx = 0; idx < total; idx++) {
      if (runLabel[idx] >= 0) {
        var c = rootOf[runLabel[idx]];
        labels[idx] = c;
        counts[c + 1]++;
      }
    }
    var compStart = new Int32Array(nComp + 1);
    for (p = 0; p < nComp; p++) compStart[p + 1] = compStart[p] + counts[p + 1];
    var cursor = new Int32Array(nComp);
    var compPix = new Int32Array(compStart[nComp]);
    for (idx = 0; idx < total; idx++) {
      var cc = labels[idx];
      if (cc >= 0) compPix[compStart[cc] + cursor[cc]++] = idx;
    }

    return { labels: labels, compStart: compStart, compPix: compPix, compCount: nComp };
  }

  // Restore one whole piece. Returns its bounding box so only that area repaints.
  //
  // THE INVARIANT LIVES HERE: alpha[i] only ever moves UP, and only ever to
  // contentSoft[i]. It cannot exceed max(alpha, contentSoft) by construction, so
  // Magic is structurally incapable of restoring background.
  function grabComponent(comps, c, alpha, contentSoft, w) {
    if (!comps || c < 0 || c >= comps.compCount) return null;
    var a = comps.compStart[c], b = comps.compStart[c + 1];
    if (b <= a) return null;
    var minX = Infinity, minY = Infinity, maxX = -1, maxY = -1;
    for (var k = a; k < b; k++) {
      var i = comps.compPix[k];
      var v = contentSoft[i];
      if (v > alpha[i]) alpha[i] = v;
      var y = (i / w) | 0, x = i - y * w;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    if (maxX < 0) return null;
    return { x0: minX, y0: minY, x1: maxX + 1, y1: maxY + 1 };
  }

  function mergeRect(a, b) {
    if (!a) return b;
    if (!b) return a;
    return {
      x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0),
      x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1)
    };
  }

  // Grab every unclaimed piece within MAGIC_RADIUS of a touch. `grabbed` is the
  // caller's per-stroke memo: a component already restored during this stroke is
  // skipped, so dragging back over a letter costs nothing.
  function magicAt(comps, grabbed, alpha, contentSoft, w, h, cx, cy, radius) {
    if (!comps || !comps.labels.length) return null;
    var r = (typeof radius === 'number' && radius > 0) ? radius : MAGIC_RADIUS;
    var x0 = Math.max(0, (cx - r) | 0);
    var x1 = Math.min(w, Math.ceil(cx + r));
    var y0 = Math.max(0, (cy - r) | 0);
    var y1 = Math.min(h, Math.ceil(cy + r));
    var box = null;
    for (var y = y0; y < y1; y++) {
      var row = y * w;
      for (var x = x0; x < x1; x++) {
        var c = comps.labels[row + x];
        if (c < 0 || grabbed[c]) continue;
        grabbed[c] = 1;
        box = mergeRect(box, grabComponent(comps, c, alpha, contentSoft, w));
      }
    }
    return box;
  }

  function magicLine(comps, grabbed, alpha, contentSoft, w, h, x0, y0, x1, y1, radius) {
    var r = (typeof radius === 'number' && radius > 0) ? radius : MAGIC_RADIUS;
    var dx = x1 - x0, dy = y1 - y0;
    var dist = Math.sqrt(dx * dx + dy * dy);
    // Step at 0.9r so consecutive probes overlap - a gap here would let a fast
    // drag skip straight over a letter.
    var n = Math.max(1, Math.ceil(dist / (r * 0.9)));
    var box = null;
    for (var i = 1; i <= n; i++) {
      box = mergeRect(box, magicAt(comps, grabbed, alpha, contentSoft, w, h,
        x0 + dx * i / n, y0 + dy * i / n, r));
    }
    return box;
  }

  // ---------------------------------------------------------------------------
  // Brush / Erase
  // ---------------------------------------------------------------------------

  // One soft-edged dab. The 0.72 shoulder is what stops a stroke reading as a
  // chain of hard discs; without it every manual repair has a visible scallop
  // along its edge, which on a printed garment is worse than the hole it fixed.
  function stamp(alpha, w, h, cx, cy, r, target) {
    var x0 = Math.floor(cx - r), x1 = Math.ceil(cx + r);
    var y0 = Math.floor(cy - r), y1 = Math.ceil(cy + r);
    var inner = r * 0.72;
    var band = Math.max(0.75, r - inner);
    var cx0 = Math.max(0, x0), cx1 = Math.min(w, x1);
    var cy0 = Math.max(0, y0), cy1 = Math.min(h, y1);
    for (var y = cy0; y < cy1; y++) {
      var dy = y - cy, row = y * w;
      for (var x = cx0; x < cx1; x++) {
        var dx = x - cx;
        var d = Math.sqrt(dx * dx + dy * dy);
        if (d > r) continue;
        var wt = d <= inner ? 1 : 1 - (d - inner) / band;
        if (wt <= 0) continue;
        var i = row + x;
        alpha[i] = alpha[i] + (target - alpha[i]) * wt;
      }
    }
    return { x0: x0, y0: y0, x1: x1, y1: y1 };
  }

  function strokeLine(alpha, w, h, x0, y0, x1, y1, r, target) {
    var dx = x1 - x0, dy = y1 - y0;
    var dist = Math.sqrt(dx * dx + dy * dy);
    var step = Math.max(1, r * 0.28);
    var n = Math.max(1, Math.ceil(dist / step));
    var box = null;
    for (var i = 1; i <= n; i++) {
      box = mergeRect(box, stamp(alpha, w, h, x0 + dx * i / n, y0 + dy * i / n, r, target));
    }
    return box;
  }

  // ---------------------------------------------------------------------------
  // Display compositing
  // ---------------------------------------------------------------------------

  // Paint the SHOWN alpha into a display RGBA buffer over one rectangle.
  //
  // shown = max(alpha, ghost). `alpha` is never read back from here, which is
  // the mechanical reason the ghost cannot leak into a saved file: the ghost
  // only ever exists in the pixels the screen owns.
  function compositeShown(frameData, alpha, ghost, ghostOn, w, h, rect) {
    var x0 = 0, y0 = 0, x1 = w, y1 = h;
    if (rect) {
      x0 = Math.max(0, rect.x0 | 0); y0 = Math.max(0, rect.y0 | 0);
      x1 = Math.min(w, Math.ceil(rect.x1)); y1 = Math.min(h, Math.ceil(rect.y1));
    }
    if (x1 <= x0 || y1 <= y0) return { x0: 0, y0: 0, x1: 0, y1: 0 };
    var useGhost = !!(ghostOn && ghost);
    for (var y = y0; y < y1; y++) {
      var row = y * w;
      for (var x = x0; x < x1; x++) {
        var i = row + x;
        var a = alpha[i];
        if (useGhost) { var g = ghost[i]; if (g > a) a = g; }
        frameData[i * 4 + 3] = a;
      }
    }
    return { x0: x0, y0: y0, x1: x1, y1: y1 };
  }

  // ---------------------------------------------------------------------------
  // Undo
  // ---------------------------------------------------------------------------

  // History is stored as RECTANGLE PATCHES, not whole-mask snapshots. At the
  // 2400px print cap one mask is 5.8 MB, so sixteen naive snapshots is 92 MB on
  // a phone that is already holding two RGBA frames and a label map. A stroke
  // touches a few thousand pixels; storing only those keeps undo depth free.
  function diffRect(before, after, w, h) {
    var minX = Infinity, minY = Infinity, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y++) {
      var row = y * w;
      for (var x = 0; x < w; x++) {
        var i = row + x;
        if (before[i] !== after[i]) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    return { x0: minX, y0: minY, x1: maxX + 1, y1: maxY + 1 };
  }

  function extractRect(alpha, w, rect) {
    var rw = rect.x1 - rect.x0, rh = rect.y1 - rect.y0;
    var out = new Uint8ClampedArray(rw * rh);
    for (var y = 0; y < rh; y++) {
      var src = (rect.y0 + y) * w + rect.x0;
      out.set(alpha.subarray(src, src + rw), y * rw);
    }
    return out;
  }

  function applyRect(alpha, w, rect, patch) {
    var rw = rect.x1 - rect.x0, rh = rect.y1 - rect.y0;
    for (var y = 0; y < rh; y++) {
      var dst = (rect.y0 + y) * w + rect.x0;
      alpha.set(patch.subarray(y * rw, y * rw + rw), dst);
    }
  }

  // ---------------------------------------------------------------------------
  // Assertions
  // ---------------------------------------------------------------------------

  /* build:strip-start */
  // NOT SHIPPED. scripts/build-protected.js removes everything between these two
  // markers and substitutes an empty __selfTest, so the export below stays valid.
  // If you move or rename this function, MOVE THE MARKERS WITH IT. Omitting the
  // STRIP_BLOCKS replacement strips the binding but leaves `__selfTest:
  // __selfTest` in the export, which throws at load and takes the whole surface
  // out silently - the exact bug found in fortee-bg-retention.js.
  //
  // Synthetic frames only. The real fixture assertions need a PNG decoder and
  // live in scripts/test-bg-touchup.cjs.
  function __selfTest() {
    var failures = [];
    var passed = 0;
    function ok(cond, label) { if (cond) { passed++; } else { failures.push(label); } }
    function eqNum(a, b, label) { ok(a === b, label + ' (got ' + a + ', want ' + b + ')'); }

    var W = 32, H = 32, N = W * H;
    function frame(fill) {
      var d = new Uint8Array(N * 4);
      for (var i = 0; i < N; i++) {
        d[i * 4] = fill[0]; d[i * 4 + 1] = fill[1]; d[i * 4 + 2] = fill[2]; d[i * 4 + 3] = 255;
      }
      return d;
    }
    function box(d, x0, y0, x1, y1, rgb) {
      for (var y = y0; y <= y1; y++) {
        for (var x = x0; x <= x1; x++) {
          var i = (y * W + x) * 4;
          d[i] = rgb[0]; d[i + 1] = rgb[1]; d[i + 2] = rgb[2];
        }
      }
      return d;
    }

    // White frame with TWO disjoint black marks - the Martel shape in miniature:
    // an engine that keeps only the biggest object deletes the second one.
    var img = frame([255, 255, 255]);
    box(img, 4, 4, 11, 11, [0, 0, 0]);      // 64 px
    box(img, 20, 20, 23, 23, [0, 0, 0]);    // 16 px

    var an = analyzeFrame(W, H, img);
    ok(!!an.profile, 'analyzeFrame returns a profile');
    ok(an.canFix === true, 'analyzeFrame allows Fix on a flat background');
    eqNum(an.contentSoft.length, N, 'contentSoft is one byte per pixel');

    var comps = buildComponents(W, H, an.contentSoft);
    eqNum(comps.compCount, 2, 'two disjoint marks label as two components');

    // Base cutout = the salient-object failure: the big mark kept, the small one
    // deleted. Magic on the small one restores exactly it.
    var alpha = new Uint8ClampedArray(N);
    var y, x;
    for (y = 4; y <= 11; y++) for (x = 4; x <= 11; x++) alpha[y * W + x] = 255;
    var base = new Uint8ClampedArray(alpha);

    var grabbed = {};
    var rect = magicAt(comps, grabbed, alpha, an.contentSoft, W, H, 21.5, 21.5);
    ok(!!rect, 'magic returns a repaint rect');
    eqNum(opaqueCount(alpha), 80, 'magic restored the whole second mark (64 + 16)');

    // THE INVARIANT.
    var overshoot = 0;
    for (var i = 0; i < N; i++) {
      var ceiling = Math.max(base[i], an.contentSoft[i]);
      if (alpha[i] > ceiling) overshoot++;
    }
    eqNum(overshoot, 0, 'INVARIANT: magic never exceeds max(base, content) - it cannot restore background');

    // A second grab of the same component is a no-op (the memo works).
    var beforeSecond = opaqueCount(alpha);
    magicAt(comps, grabbed, alpha, an.contentSoft, W, H, 21.5, 21.5);
    eqNum(opaqueCount(alpha), beforeSecond, 'a re-grab of the same piece changes nothing');

    // Ghost never leaks: composite twice, compare the MASK not the display.
    var disp = new Uint8ClampedArray(img);
    var maskBefore = new Uint8ClampedArray(alpha);
    compositeShown(disp, alpha, an.ghost, true, W, H, null);
    compositeShown(disp, alpha, an.ghost, false, W, H, null);
    var same = true;
    for (i = 0; i < N; i++) if (maskBefore[i] !== alpha[i]) same = false;
    ok(same, 'GHOST: compositing with the ghost on or off leaves the true mask identical');

    // ...and the ghost really does change the SCREEN, or the assertion above
    // would pass for the trivial reason that the ghost does nothing at all.
    var dispOn = new Uint8ClampedArray(img);
    compositeShown(dispOn, alpha, an.ghost, true, W, H, null);
    var dispOff = new Uint8ClampedArray(img);
    compositeShown(dispOff, alpha, an.ghost, false, W, H, null);
    var differs = false;
    for (i = 0; i < N; i++) if (dispOn[i * 4 + 3] !== dispOff[i * 4 + 3]) differs = true;
    // Both marks are fully restored here, so nothing is missing to ghost. Erase
    // one first, then the ghost has something to show.
    stamp(alpha, W, H, 21.5, 21.5, 4, 0);
    dispOn = new Uint8ClampedArray(img);
    compositeShown(dispOn, alpha, an.ghost, true, W, H, null);
    dispOff = new Uint8ClampedArray(img);
    compositeShown(dispOff, alpha, an.ghost, false, W, H, null);
    differs = false;
    for (i = 0; i < N; i++) if (dispOn[i * 4 + 3] !== dispOff[i * 4 + 3]) differs = true;
    ok(differs, 'GHOST: the ghost is visible on screen where artwork was erased');

    // Undo, exactly.
    var beforeStroke = new Uint8ClampedArray(alpha);
    stamp(alpha, W, H, 8, 8, 3, 0);
    var r2 = diffRect(beforeStroke, alpha, W, H);
    ok(!!r2, 'diffRect finds the touched rectangle');
    var patch = extractRect(beforeStroke, W, r2);
    applyRect(alpha, W, r2, patch);
    var exact = true;
    for (i = 0; i < N; i++) if (alpha[i] !== beforeStroke[i]) exact = false;
    ok(exact, 'UNDO: a rect patch restores the mask exactly');
    ok(diffRect(beforeStroke, beforeStroke, W, H) === null, 'diffRect on identical masks is null');

    // Cutout detection.
    ok(isRealCutout(base) === true, 'a real cutout is detected as one');
    var opaqueAll = new Uint8ClampedArray(N);
    opaqueAll.fill(255);
    ok(isRealCutout(opaqueAll) === false, 'a fully opaque frame is NOT a cutout');

    // Resampling a half-size candidate.
    var halfW = 16, halfH = 16;
    var half = new Uint8Array(halfW * halfH * 4);
    for (y = 2; y <= 5; y++) for (x = 2; x <= 5; x++) half[(y * halfW + x) * 4 + 3] = 255;
    var up = sampleAlphaOnto(W, H, halfW, halfH, half);
    eqNum(up.length, N, 'sampleAlphaOnto returns the destination grid');
    eqNum(up[4 * W + 4], 255, 'sampleAlphaOnto maps a half-res candidate onto the original');
    eqNum(up[0], 0, 'sampleAlphaOnto leaves cleared pixels cleared');

    // Photographic background: the Fix affordance must not be offered.
    var noise = new Uint8Array(N * 4);
    var seed = 12345;
    for (i = 0; i < N; i++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      noise[i * 4] = seed & 0xff;
      noise[i * 4 + 1] = (seed >> 8) & 0xff;
      noise[i * 4 + 2] = (seed >> 16) & 0xff;
      noise[i * 4 + 3] = 255;
    }
    var anNoise = analyzeFrame(W, H, noise);
    ok(anNoise.canFix === false, 'THE GATE: Fix is not offered on a photographic background');

    return { passed: passed, failed: failures.length, failures: failures };
  }
  /* build:strip-end */

  var api = {
    GHOST_ALPHA: GHOST_ALPHA,
    MAGIC_RADIUS: MAGIC_RADIUS,
    OPAQUE_ALPHA: OPAQUE_ALPHA,
    CUTOUT_MIN_TRANSPARENT: CUTOUT_MIN_TRANSPARENT,
    analyzeFrame: analyzeFrame,
    sampleAlphaOnto: sampleAlphaOnto,
    transparentShare: transparentShare,
    isRealCutout: isRealCutout,
    opaqueCount: opaqueCount,
    buildComponents: buildComponents,
    grabComponent: grabComponent,
    magicAt: magicAt,
    magicLine: magicLine,
    mergeRect: mergeRect,
    stamp: stamp,
    strokeLine: strokeLine,
    compositeShown: compositeShown,
    diffRect: diffRect,
    extractRect: extractRect,
    applyRect: applyRect,
    __selfTest: __selfTest
  };

  if (typeof window !== 'undefined') window.forteeBgTouchup = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
